/*
  * Copyright Amazon.com, Inc. and its affiliates. All Rights Reserved.
  * SPDX-License-Identifier: MIT
  *
  * Licensed under the MIT License. See the LICENSE accompanying this file
  * for the specific language governing permissions and limitations under
  * the License.
  */

import React from 'react';

import { Auth } from 'aws-amplify';
import { I18n } from '@aws-amplify/core';

import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import { Box, Typography } from '@material-ui/core';

import { Branding } from '../../branding';
import { getCurrentBrand } from '../../utils/applyBrand';
import AppSnackbar from '../../components/Snackbar/Snackbar';
import FormInlineError from '../FormInlineError/FormInlineError';
import PasswordInputField, { isPasswordMatch, validatePasswordRules, PASSWORD_RULES } from '../PasswordInputField/PasswordInputField';
import { translateFormErrorMessage } from '../../utils/formErrorMessage';
import { readInputValueById } from '../../utils/reactFormSync';

const useStyles = makeStyles((theme) => ({
	dialogActions: {
		paddingRight: 22,
	},
	box: {
		textAlign: 'center',
		marginBottom: 20,
	},
	inlineRoot: {
		width: '100%',
		textAlign: 'left',
	},
	inlinePageTitle: {
		marginBottom: theme.spacing(1),
		fontWeight: 600,
		color: theme.palette.text.primary,
		textAlign: 'left',
		width: '100%',
	},
	inlineDescription: {
		marginBottom: theme.spacing(2),
		textAlign: 'left',
	},
	inlineFieldBlock: {
		marginBottom: theme.spacing(2),
		textAlign: 'left',
		width: '100%',
	},
	inlineFooter: {
		marginTop: theme.spacing(3),
	},
	input: {
		minWidth: 300,
	},
	inlineInput: {
		width: '100%',
		minWidth: 0,
	},
	buttonSave: {
		color: Branding.positive,
	},
	buttonCancel: {
		color: Branding.negative,
	},
	buttonCancelNeutralInline: {
		color: theme.palette.grey[700],
		borderColor: theme.palette.grey[400],
		'&:hover': {
			borderColor: theme.palette.grey[500],
			backgroundColor: theme.palette.grey[50],
		},
	},
	passwordHint: {
		fontSize: '0.8rem',
		color: '#666',
		marginBottom: 8,
		textAlign: 'left',
	},
	validationError: {
		fontSize: '0.8rem',
		color: Branding.negative,
		marginTop: 4,
		textAlign: 'left',
	},
	validationSuccess: {
		fontSize: '0.8rem',
		color: Branding.positive,
		marginTop: 4,
		textAlign: 'left',
	},
	// MUI containedPrimary より後勝ち（body クラス未同期時もロゴと同色に揃える）
	inlineSaveContained: {
		'&&': {
			backgroundColor: '#fcb900',
			color: '#fff',
			boxShadow: 'none',
		},
		'&&:hover': {
			backgroundColor: '#fcb900',
			opacity: 0.8,
			boxShadow: 'none',
		},
		'&.Mui-disabled': {
			backgroundColor: '#ffac31',
			color: '#fff',
		},
	},
	inlineSaveContainedEvents: {
		'&&': {
			backgroundColor: '#9ec317',
			color: '#fff',
			boxShadow: 'none',
		},
		'&&:hover': {
			backgroundColor: '#9ec317',
			opacity: 0.8,
			boxShadow: 'none',
		},
		'&.Mui-disabled': {
			opacity: 0.65,
			color: '#fff',
			backgroundColor: '#9ec317',
		},
	},
}));

/**
 * @param {object} props
 * @param {boolean} [props.inline] — true のとき /settings/password 用のページ内フォーム（ダイアログなし）
 * @param {boolean} props.open — ダイアログ表示時のみ使用
 * @param {(successful?: boolean) => void} props.close
 */
const ChangePasswordDialog = (props) => {
	const classes = useStyles();
	const inline = props.inline === true;
	const brand = getCurrentBrand();
	const [oldPassword, setOldPassword] = React.useState({
		password: '',
		showPassword: false,
	});
	const [newPassword, setNewPassword] = React.useState({
		password: '',
		showPassword: false,
	});
	const [confirmPassword, setConfirmPassword] = React.useState({
		password: '',
		showPassword: false,
	});
	const [snackBarOps, setSnackBarOps] = React.useState({
		type: 'info',
		open: false,
		vertical: 'top',
		horizontal: 'center',
		autoHide: 0,
		message: '',
	});
	const [formError, setFormError] = React.useState('');

	const showFormError = (message) => {
		setFormError(translateFormErrorMessage(message));
	};

	const passwordValidation = validatePasswordRules(newPassword.password);
	const passwordsMatch = isPasswordMatch(newPassword.password, confirmPassword.password);

	const changePassword = (oldPwd, newPwd) => {
		if (!oldPwd || !newPwd) {
			showFormError(I18n.get('CHANGE_PASSWORD_MESSAGE_EROR'));
			return;
		}

		const validation = validatePasswordRules(newPwd);
		if (!validation.isValid) {
			showFormError(validation.messages.join('、'));
			return;
		}

		setFormError('');

		Auth.currentAuthenticatedUser()
			.then((CognitoUser) => {
				Auth.changePassword(CognitoUser, oldPwd, newPwd)
					.then((data) => {
						handleCancel(data === 'SUCCESS');
					})
					.catch((err) => {
						console.log(err);
						showFormError(I18n.get('CHANGE_PASSWORD_MESSAGE_EROR'));
					});
			})
			.catch((err) => {
				console.log(err);
				showFormError(I18n.get('CHANGE_PASSWORD_MESSAGE_EROR'));
			});
	};

	const handleClickSave = () => {
		const resolvedOld = readInputValueById('inputOldPassword', oldPassword.password);
		const resolvedNew = readInputValueById('inputNewPassword', newPassword.password);
		const resolvedConfirm = readInputValueById('inputConfirmPassword', confirmPassword.password);
		setOldPassword({ ...oldPassword, password: resolvedOld });
		setNewPassword({ ...newPassword, password: resolvedNew });
		setConfirmPassword({ ...confirmPassword, password: resolvedConfirm });

		if (!isPasswordMatch(resolvedNew, resolvedConfirm)) {
			showFormError(I18n.get('CHANGE_PASSWORD_MESSAGE_EROR'));
			return;
		}
		changePassword(resolvedOld, resolvedNew);
	};

	const handleCancel = (succesful = false) => {
		setOldPassword({ ...oldPassword, password: '' });
		setNewPassword({ ...newPassword, password: '' });
		setConfirmPassword({ ...confirmPassword, password: '' });
		setFormError('');
		props.close(succesful);
	};

	const boxClass = inline ? classes.inlineFieldBlock : classes.box;
	const inputClassName = inline ? classes.inlineInput : classes.input;
	const passwordFieldVariant = inline ? 'outlined' : 'standard';

	const formFields = (
		<>
			<Box className={boxClass}>
				<PasswordInputField
					id="inputOldPassword"
					label={I18n.get('CHANGE_PASSWORD_OLDPASSWORD_INPUT_LABEL')}
					value={oldPassword.password}
					showPassword={oldPassword.showPassword}
					onChange={(value) => setOldPassword({ ...oldPassword, password: value })}
					onToggleShowPassword={() =>
						setOldPassword({ ...oldPassword, showPassword: !oldPassword.showPassword })
					}
					autoFocus
					inputClassName={inputClassName}
					variant={passwordFieldVariant}
				/>
			</Box>
			<Box className={boxClass}>
				<Typography className={classes.passwordHint}>
					※ {PASSWORD_RULES.MIN_LENGTH}〜{PASSWORD_RULES.MAX_LENGTH}文字、英大文字・小文字・数字・記号のうち2種類以上
				</Typography>
				<PasswordInputField
					id="inputNewPassword"
					label={I18n.get('CHANGE_PASSWORD_NEWPASSWORD_INPUT_LABEL')}
					value={newPassword.password}
					showPassword={newPassword.showPassword}
					onChange={(value) => setNewPassword({ ...newPassword, password: value })}
					onToggleShowPassword={() =>
						setNewPassword({ ...newPassword, showPassword: !newPassword.showPassword })
					}
					inputClassName={inputClassName}
					variant={passwordFieldVariant}
				/>
				{newPassword.password && !passwordValidation.isValid && (
					<div>
						{passwordValidation.messages.map((msg, index) => (
							<Typography key={index} className={classes.validationError}>
								{msg}
							</Typography>
						))}
					</div>
				)}
				{newPassword.password && passwordValidation.isValid && (
					<Typography className={classes.validationSuccess}>パスワードルールを満たしています</Typography>
				)}
			</Box>
			<Box className={boxClass}>
				<PasswordInputField
					id="inputConfirmPassword"
					label={I18n.get('CHANGE_PASSWORD_CONFIRM_PASSWORD_INPUT_LABEL')}
					value={confirmPassword.password}
					showPassword={confirmPassword.showPassword}
					onChange={(value) => setConfirmPassword({ ...confirmPassword, password: value })}
					onToggleShowPassword={() =>
						setConfirmPassword({
							...confirmPassword,
							showPassword: !confirmPassword.showPassword,
						})
					}
					inputClassName={inputClassName}
					variant={passwordFieldVariant}
				/>
				{confirmPassword.password && !passwordsMatch && (
					<Typography className={classes.validationError}>パスワードが一致しません</Typography>
				)}
				{confirmPassword.password && passwordsMatch && (
					<Typography className={classes.validationSuccess}>パスワードが一致しています</Typography>
				)}
			</Box>
		</>
	);

	const snackbar = snackBarOps.open && snackBarOps.type !== 'error' && <AppSnackbar ops={snackBarOps} />;

	if (inline) {
		return (
			<div>
				{snackbar}
				<div className={classes.inlineRoot}>
					<FormInlineError message={formError} />
					<Typography variant="h6" component="h1" className={classes.inlinePageTitle}>
						{I18n.get('CHANGE_PASSWORD_TITLE')}
					</Typography>
					<Typography variant="body2" color="textSecondary" className={classes.inlineDescription}>
						{I18n.get('CHANGE_PASSWORD_DESCRIPTION')}
					</Typography>
					{formFields}
					<div className={`login-footer-actions ${classes.inlineFooter}`}>
						<Button
							variant="contained"
							color="primary"
							fullWidth
							className={
								brand === 'halmekEvents' ? classes.inlineSaveContainedEvents : classes.inlineSaveContained
							}
							onClick={handleClickSave}
						>
							{I18n.get('CHANGE_PASSWORD_SAVE_BUTTON_LABEL')}
						</Button>
						<Button
							variant="outlined"
							fullWidth
							onClick={() => handleCancel(false)}
							className={classes.buttonCancelNeutralInline}
						>
							{I18n.get('CHANGE_PASSWORD_CANCEL_BUTTON_LABEL')}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div>
			{snackbar}

			<Dialog
				open={props.open}
				onClose={() => handleCancel(false)}
				disableBackdropClick
				aria-labelledby="change-password-dialog"
			>
				<DialogTitle id="change-password-dialog-title">{I18n.get('CHANGE_PASSWORD_TITLE')}</DialogTitle>
				<FormInlineError message={formError} />
				<DialogContent>
					<DialogContentText>{I18n.get('CHANGE_PASSWORD_DESCRIPTION')}</DialogContentText>
					{formFields}
				</DialogContent>
				<DialogActions className={classes.dialogActions}>
					<Button variant="outlined" onClick={handleClickSave} className={classes.buttonSave}>
						{I18n.get('CHANGE_PASSWORD_SAVE_BUTTON_LABEL')}
					</Button>
					<Button variant="outlined" onClick={() => handleCancel(false)} className={classes.buttonCancel}>
						{I18n.get('CHANGE_PASSWORD_CANCEL_BUTTON_LABEL')}
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default ChangePasswordDialog;
